"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import type { EChartsOption } from "echarts";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type Props = {
  title: string;
  data: { name: string; value: number }[];
  palette: string[];
  legendBottom?: number;
  legendAlign?: "center" | "left";
};

export default function ClauseDonutChart({
  title,
  data,
  palette,
  legendBottom = 0,
  legendAlign = "center",
}: Props) {
  const isMobile = useMediaQuery("(max-width:400px)");

  const sortedLegendNames = useMemo(
    () => [...data].map((d) => d.name).sort((a, b) => a.localeCompare(b)),
    [data],
  );

  const legendRows = useMemo(
    () => ({
      first: sortedLegendNames.slice(0, 3),
      second: sortedLegendNames.slice(3, 6),
    }),
    [sortedLegendNames],
  );

  const mobileLegendTop = 240;
  const mobileChartHeight = Math.max(
    400,
    mobileLegendTop + sortedLegendNames.length * 24 + 16,
  );

  const option = useMemo<EChartsOption>(
    () => ({
      tooltip: {
        trigger: "item",
        formatter: (params: unknown) => {
          const p = params as {
            name?: string;
            value?: number;
            percent?: number;
          };
          return `${p.name ?? ""}<br/><b>${p.value ?? 0} documents</b> (${(p.percent ?? 0).toFixed(0)}%)`;
        },
      },
      legend: isMobile
        ? {
            orient: "vertical",
            left: "center",
            width: "92%",
            top: mobileLegendTop,
            itemGap: 8,
            itemWidth: 16,
            itemHeight: 10,
            textStyle: { fontSize: 12 },
            data: sortedLegendNames,
          }
        : [
            {
              bottom: legendBottom + 22,
              left: legendAlign,
              itemGap: 13,
              itemWidth: 18,
              itemHeight: 10,
              textStyle: { fontSize: 12 },
              data: legendRows.first,
            },
            {
              bottom: legendBottom,
              left: legendAlign,
              itemGap: 13,
              itemWidth: 18,
              itemHeight: 10,
              textStyle: { fontSize: 12 },
              data: legendRows.second,
            },
          ],
      series: [
        {
          type: "pie",
          radius: isMobile ? ["32%", "56%"] : ["42%", "78%"],
          center: isMobile ? ["50%", "30%"] : ["50%", "45%"],
          bottom: isMobile ? 0 : 28,
          data: data.map((d, i) => ({
            ...d,
            itemStyle: { color: palette[i % palette.length] },
          })),
          label: { show: false },
          emphasis: { scale: true },
        },
      ],
    }),
    [
      data,
      isMobile,
      legendAlign,
      legendBottom,
      legendRows,
      palette,
      sortedLegendNames,
      mobileLegendTop,
    ],
  );

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, color: "#363636", mb: 1 }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 1.5 }} />
      <ReactECharts
        option={option}
        style={{ height: isMobile ? mobileChartHeight : 220, width: "100%" }}
        notMerge
      />
    </Box>
  );
}
